/**
 * Manufacturing management — two-column sales order details (list + header/ship-to/lines).
 */

import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Loader2 } from "lucide-react";

import { BatchDetailLink } from "@/components/hub/HubEntityDetailLink";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import {
  buildInsideSalesApprovalPath,
  buildInsideSalesSalesOrderEditPath,
  buildSalesOrderDetailPath,
  buildShippingGosiDetailsPath,
  buildShippingShipmentDetailsPath,
} from "@/lib/hubEntityDetailPaths";
import {
  fetchManufacturingSalesOrderDetail,
  fetchManufacturingSalesOrderDetails,
  type ManufacturingSalesOrderDetailRow,
  type ManufacturingSalesOrderRelated,
} from "@/lib/manufacturingSalesOrderDetails";
import { useHubModuleHasAnyAccess } from "@/lib/useHubModuleWrite";
import { cn } from "@/lib/utils";
import { salesOrderStatusLabel } from "@/lib/salesOrderHubStatus";

const quantityFormatter = new Intl.NumberFormat();

function megaventorySalesOrderUrl(mvId: number, salesOrderNumber: string): string {
  const name = encodeURIComponent(salesOrderNumber.trim() !== "" ? salesOrderNumber.trim() : `Sales Order ${mvId}`);
  return `https://smartgridsolutions.megaventory.com/inventory/default.aspx?document=SalesOrder&id=${mvId}&name=${name}`;
}

function DetailField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm text-slate-800">{value}</dd>
    </div>
  );
}

function SalesOrderDetailsPanel({
  row,
  related,
}: {
  row: ManufacturingSalesOrderDetailRow;
  related: ManufacturingSalesOrderRelated | null;
}) {
  const canOpenInsideSalesEditor = useHubModuleHasAnyAccess("inside_sales");
  const items = row.items ?? [];
  const lineLinks = related?.lines ?? [];

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold text-slate-900">{row.number}</h2>
          {row.status ? <Badge tone="neutral">{salesOrderStatusLabel(row.status)}</Badge> : null}
          {row.mv_id != null ? (
            <a
              href={megaventorySalesOrderUrl(row.mv_id, row.number)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-brand-700 hover:text-brand-800"
            >
              Open in Megaventory
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          ) : null}
        </div>
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <DetailField label="Customer" value={row.client_fs_account_name ?? "—"} />
          <DetailField label="End user" value={row.end_user_fs_account_name ?? "—"} />
          <DetailField label="Ship by" value={row.sgs_ship_by_date ?? "—"} />
          <DetailField label="Ship to" value={row.ship_to_recipient ?? "—"} />
          <DetailField
            label="Ship to city / state"
            value={[row.ship_to_city, row.ship_to_state].filter(Boolean).join(", ") || "—"}
          />
          <DetailField label="Payment terms" value={row.payment_terms ?? "—"} />
          <DetailField label="Customer PO" value={row.customer_purchase_order_number ?? "—"} />
        </dl>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-900">Lines</h3>
        {items.length === 0 ? (
          <p className="text-sm text-slate-500">No sales order item rows attached.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-xs font-semibold uppercase tracking-normal text-slate-500">
                <tr>
                  <th className="pb-2 pr-4">SKU</th>
                  <th className="pb-2 pr-4 text-right">Qty</th>
                  <th className="pb-2 pr-4 text-right">Assembled</th>
                  <th className="pb-2 pr-4 text-right">Shipped</th>
                  <th className="pb-2 pr-4">Batch</th>
                  <th className="pb-2">Remark</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((item) => {
                  const links = lineLinks.filter((line) => line.salesOrderItemId === item.id);
                  const assembled = links.reduce((sum, line) => sum + line.quantityAssembled, 0);
                  const shipped = links.reduce((sum, line) => sum + line.quantityShipped, 0);
                  return (
                    <tr key={item.id}>
                      <td className="py-2 pr-4 font-medium text-slate-900">{item.sku}</td>
                      <td className="py-2 pr-4 text-right tabular-nums text-slate-700">
                        {quantityFormatter.format(item.quantity)}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums text-slate-700">
                        {quantityFormatter.format(assembled)}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums text-slate-700">
                        {quantityFormatter.format(shipped)}
                      </td>
                      <td className="py-2 pr-4 text-slate-700">
                        {links.length === 0
                          ? "—"
                          : links.map((line) => (
                              <span key={line.batchId} className="mr-2">
                                <BatchDetailLink batchId={line.batchId}>{line.batchId}</BatchDetailLink>
                              </span>
                            ))}
                      </td>
                      <td className="py-2 text-slate-600">{item.remark ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {row.special_instructions ? (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-900">Special instructions</h3>
          <p className="whitespace-pre-wrap text-sm text-slate-700">{row.special_instructions}</p>
        </section>
      ) : null}

      {related != null ? (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-900">Shipments and invoices</h3>
          {related.shipments.length === 0 && related.invoices.length === 0 ? (
            <p className="text-sm text-slate-500">No shipments or sales invoices are linked yet.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {related.shipments.map((shipment) => (
                <li key={shipment.id}>
                  <Link className="text-brand-700 hover:text-brand-800" to={buildShippingShipmentDetailsPath(shipment.id)}>
                    Shipment {shipment.id}
                  </Link>
                  <span className="text-slate-500"> from </span>
                  <BatchDetailLink batchId={shipment.batchId}>{`batch ${shipment.batchId}`}</BatchDetailLink>
                </li>
              ))}
              {related.invoices.map((invoice) => (
                <li key={`${invoice.shipmentId}-${invoice.invoiceMvId}`}>
                  <Link className="text-brand-700 hover:text-brand-800" to={buildShippingGosiDetailsPath(invoice.invoiceMvId)}>
                    Sales invoice {invoice.invoiceMvId}
                  </Link>
                  <span className="text-slate-500"> on shipment {invoice.shipmentId}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {related?.approvalTicketId != null ? (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-900">Approval</h3>
          <Link className="text-sm text-brand-700 hover:text-brand-800" to={buildInsideSalesApprovalPath(related.approvalTicketId)}>
            View approval
          </Link>
          {related.documents.length > 0 ? (
            <ul className="space-y-1 text-sm text-slate-700">
              {related.documents.map((document) => (
                <li key={document.id}>
                  {document.kindLabel}: {document.fileName}
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}

      {related != null && related.priorOrders.length > 0 ? (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-900">Earlier orders for this end user</h3>
          <ul className="space-y-1 text-sm">
            {related.priorOrders.map((prior) => (
              <li key={`${prior.salesOrderId}-${prior.sku}`}>
                <Link className="text-brand-700 hover:text-brand-800" to={buildSalesOrderDetailPath(prior.salesOrderId)}>
                  {prior.salesOrderNumber}
                </Link>
                <span className="text-slate-600">
                  {" "}
                  {prior.sku} × {quantityFormatter.format(prior.quantity)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {related != null && related.activity.length > 0 ? (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-900">History</h3>
          <ul className="space-y-2 text-sm text-slate-700">
            {related.activity.map((entry) => (
              <li key={entry.id}>
                <span className="font-medium text-slate-900">{entry.action}</span>
                {entry.actorDisplayName ? <span> by {entry.actorDisplayName}</span> : null}
                {entry.detail ? <span className="block text-slate-600">{entry.detail}</span> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {canOpenInsideSalesEditor ? (
        <Link to={buildInsideSalesSalesOrderEditPath(row.number)} className="inline-flex">
          <Button type="button" variant="secondary" size="sm">
            Edit sales order
          </Button>
        </Link>
      ) : null}
    </div>
  );
}

export function ManufacturingSalesOrderDetailsPage() {
  const navigate = useNavigate();
  const { salesOrderId: salesOrderIdRaw } = useParams<{ salesOrderId?: string }>();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const selectedFromPath = salesOrderIdRaw != null ? Number.parseInt(salesOrderIdRaw, 10) : NaN;
  const selectedFromQuery = searchParams.get("id") != null ? Number.parseInt(searchParams.get("id")!, 10) : NaN;
  const selectedId = Number.isFinite(selectedFromPath) && selectedFromPath > 0
    ? selectedFromPath
    : Number.isFinite(selectedFromQuery) && selectedFromQuery > 0
      ? selectedFromQuery
      : null;

  const listQuery = useQuery<ManufacturingSalesOrderDetailRow[], ApiError>({
    queryKey: ["manufacturing", "sales-order-details", search],
    queryFn: () => fetchManufacturingSalesOrderDetails(search),
  });

  const detailQuery = useQuery<{ row: ManufacturingSalesOrderDetailRow; related: ManufacturingSalesOrderRelated }, ApiError>({
    queryKey: ["manufacturing", "sales-order-details", "detail", selectedId],
    queryFn: () => fetchManufacturingSalesOrderDetail(selectedId!),
    enabled: selectedId != null,
  });

  const selectedRow =
    detailQuery.data?.row ?? listQuery.data?.find((row) => Number(row.id) === selectedId) ?? null;
  const rows = useMemo(() => listQuery.data ?? [], [listQuery.data]);

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint="Browse sales orders and inspect customer, ship-to, and line details. Incoming orders remains the unbatched scheduling queue.">
        Sales order details
      </PageTitleWithHelp>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <CardTitle>Orders</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search number, customer, end user…"
            />
            {listQuery.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </div>
            ) : null}
            <ul className="max-h-[70vh] space-y-1 overflow-y-auto">
              {rows.map((row) => {
                const rowId = Number(row.id);
                return (
                  <li key={rowId}>
                    <button
                      type="button"
                      className={cn(
                        "w-full rounded-md px-3 py-2 text-left text-sm hover:bg-slate-50",
                        selectedId === rowId && "bg-brand-50 ring-1 ring-brand-200",
                      )}
                      onClick={() => navigate(buildSalesOrderDetailPath(rowId))}
                    >
                      <div className="font-medium text-slate-900">{row.number}</div>
                      <div className="text-xs text-slate-500">{row.status ?? "Unknown"}</div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardBody>
            {selectedId == null ? (
              <p className="text-sm text-slate-500">Select a sales order.</p>
            ) : detailQuery.isLoading && selectedRow == null ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </div>
            ) : selectedRow == null ? (
              <p className="text-sm text-slate-600">Sales order not found.</p>
            ) : (
              <SalesOrderDetailsPanel row={selectedRow} related={detailQuery.data?.related ?? null} />
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
